import { __decorate } from "tslib";
import { html, observable, repeat } from '@microsoft/fast-element';
import { DI } from '@microsoft/fast-foundation';
import { AppRoutesBehavior, AppStoresBehavior, AppStylesTargetBehavior } from './behaviors';
import { AppConfig } from './config';
import { assetPredicate, registrationPredicate, targetIdPredicate } from './utils';
/**
 * Default App implementation.
 * @public
 */
export class DefaultApp {
    constructor() {
        /** {@inheritDoc AppAssets.elements} */
        this.elements = [];
        /** {@inheritDoc AppAssets.metadata} */
        this.metadata = [];
        /** {@inheritDoc AppAssets.registrations} */
        this.registrations = [];
        /** {@inheritDoc AppAssets.routes} */
        this.routes = [];
        /** {@inheritDoc AppAssets.styles} */
        this.styles = [];
        /** {@inheritDoc AppAssets.stores} */
        this.stores = [];
    }
    /** {@inheritDoc AppRegister.registerElementsTarget} */
    registerElementsTarget(targetId, predicate, repeatOptions = this.config.defaultElementRepeatOptions) {
        return html `
      ${repeat(() => this.elements
            .filter((asset) => targetIdPredicate(asset, targetId) &&
            assetPredicate(asset, this.config.context) &&
            registrationPredicate(asset, predicate, this.config.context))
            .map((token) => token.elements), html `
          ${(element) => element}
        `, repeatOptions)}
    `;
    }
    /** {@inheritDoc AppRegister.registerElement} */
    registerElement(element) {
        this.elements.push(element);
    }
    /** {@inheritDoc AppRegister.deregisterElement} */
    deregisterElement(element) {
        const index = this.elements.indexOf(element);
        if (index !== -1) {
            this.elements.splice(index, 1);
        }
    }
    /** {@inheritDoc AppRegister.registerRouteCollection} */
    registerRouteCollection(routeCollection, predicate) {
        const behaviour = new AppRoutesBehavior(this, routeCollection, predicate);
        behaviour.bind();
        return behaviour.unbind;
    }
    /** {@inheritDoc AppRegister.registerRoute} */
    registerRoute(route) {
        this.routes.push(route);
    }
    /** {@inheritDoc AppRegister.deregisterRoute} */
    deregisterRoute(route) {
        const index = this.routes.indexOf(route);
        if (index !== -1) {
            this.routes.splice(index, 1);
        }
    }
    /** {@inheritDoc AppRegister.registerStoreRoot} */
    registerStoreRoot(storeRoot, predicate) {
        const behaviour = new AppStoresBehavior(this, storeRoot, predicate);
        behaviour.bind();
        return behaviour.unbind;
    }
    /** {@inheritDoc AppRegister.registerStore} */
    registerStore(store) {
        this.stores.push(store);
    }
    /** {@inheritDoc AppRegister.deregisterStore} */
    deregisterStore(store) {
        const index = this.stores.indexOf(store);
        if (index !== -1) {
            this.stores.splice(index, 1);
        }
    }
    /** {@inheritDoc AppRegister.registerStylesTarget} */
    registerStylesTarget(targetId, predicate) {
        return new AppStylesTargetBehavior(this, targetId, predicate);
    }
    /** {@inheritDoc AppRegister.registerStyle} */
    registerStyle(style) {
        this.styles.push(style);
    }
    /** {@inheritDoc AppRegister.deregisterStyle} */
    deregisterStyle(style) {
        const index = this.styles.indexOf(style);
        if (index !== -1) {
            this.styles.splice(index, 1);
        }
    }
    /** {@inheritDoc AppRegister.registerComponents} */
    registerComponents(context) {
        const registrationContext = Object.assign(Object.assign({}, this.config.context), context);
        this.registrations.forEach((registerFn) => registerFn(registrationContext));
    }
    /**
     * {@inheritDoc AppValidity.isValid}
     *
     * @privateRemarks
     * This is a placeholder to potentially monitor metadata etc. as assets are registered and deregistered.
     */
    isValid(context) {
        return true;
    }
    /** {@inheritDoc App.registerAssets} */
    registerAssets(assets) {
        if (this.hasAssets()) {
            throw new Error(`App.registerAssets should only be called once. If this call is intentional, please call App.deregisterAssets first.`);
        }
        this.elements = assets.elements;
        this.metadata = assets.metadata;
        this.registrations = assets.registrations;
        this.routes = assets.routes;
        this.styles = assets.styles;
        this.stores = assets.stores;
    }
    /** {@inheritDoc App.deregisterAssets} */
    deregisterAssets() {
        this.elements = [];
        this.metadata = [];
        this.registrations = [];
        this.routes = [];
        this.styles = [];
        this.stores = [];
    }
    /** {@inheritDoc App.hasAssets} */
    hasAssets() {
        return (!!this.elements.length ||
            !!this.metadata.length ||
            !!this.registrations.length ||
            !!this.routes.length ||
            !!this.styles.length ||
            !!this.stores.length);
    }
}
__decorate([
    AppConfig
], DefaultApp.prototype, "config", void 0);
__decorate([
    observable
], DefaultApp.prototype, "elements", void 0);
__decorate([
    observable
], DefaultApp.prototype, "metadata", void 0);
__decorate([
    observable
], DefaultApp.prototype, "registrations", void 0);
__decorate([
    observable
], DefaultApp.prototype, "routes", void 0);
__decorate([
    observable
], DefaultApp.prototype, "styles", void 0);
__decorate([
    observable
], DefaultApp.prototype, "stores", void 0);
/**
 * App DI key.
 *
 * @privateRemarks
 * Marked as internal to stop api-extractor becoming confused cross-linking tokens with the same name.
 *
 * @internal
 */
export const App = DI.createInterface((x) => x.singleton(DefaultApp));
/**
 * Gets App from the DI container.
 *
 * @remarks
 * A utility method for host applications that are not using decorators or the DI container.
 *
 * @example
 * ```ts
 * import { getApp } from '@genesislcap/foundation-shell/app';
 * ...
 * private user = getApp();
 * ```
 *
 * @public
 */
export function getApp() {
    return DI.getOrCreateDOMContainer().get(App);
}
